import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useEleosAuth } from '@common-ui-library/eleos-login';
import { logger, useBIEvents } from '@common-ui-library/shared';
import { AuthErrorPage } from './AuthErrorPage';
import { BI_AUTH_SSO_ERROR_VIEW } from '../Utils/biEvents';

interface AuthErrorInterceptorProps {
  children: React.ReactNode;
}

/**
 * AuthErrorInterceptor intercepts Auth0 SSO errors from URL parameters
 * before the ProtectedRoute can trigger an infinite login loop.
 *
 * When Auth0 authentication fails (e.g., username_missing, organization membership issues),
 * it redirects back to the app with error parameters:
 * ?error=access_denied&error_description=username_missing&state=...
 *
 * This component catches those errors and displays a user-friendly error page
 * instead of letting the ProtectedRoute trigger another login attempt.
 */
export const AuthErrorInterceptor: React.FC<AuthErrorInterceptorProps> = ({ children }) => {
  const location = useLocation();
  const { logout } = useEleosAuth();
  const { sendBIEvent } = useBIEvents();

  const authError = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      return {
        error,
        errorDescription: errorDescription || 'Unknown error',
      };
    }

    return null;
  }, [location.search]);

  // Emit BI event when auth error is detected
  useEffect(() => {
    if (authError) {
      sendBIEvent(BI_AUTH_SSO_ERROR_VIEW, {
        message: authError.errorDescription,
      });
    }
  }, [authError, sendBIEvent]);

  const handleRetry = useCallback(() => {
    // Clear the Auth0 session first
    logout();

    // Force navigation to clean URL after logout initiates.
    // This is needed because logout() is async and may not trigger
    // an immediate redirect depending on Auth0 configuration.
    // Using 'replace' prevents back-button navigation to error page.
    // If logout does redirect first, this timeout is harmless.
    setTimeout(() => {
      window.location.replace(window.location.origin);
    }, 500);
  }, [logout]);

  if (authError) {
    logger.error('Auth0 SSO error intercepted', authError);
    return (
      <AuthErrorPage
        error={authError.error}
        errorDescription={authError.errorDescription}
        onRetry={handleRetry}
      />
    );
  }

  return <>{children}</>;
};

